import { useState, useEffect } from 'react';
import { observeOverflowChanges } from '../utils/overflowObserver';
import { getCollapsedHasOverflow } from '../utils';

interface IUseOverflowDetectionOptions {
  containerRef: React.RefObject<HTMLDivElement>;
  enabled: boolean;
  useSticky: boolean;
  isStickyFixed: boolean;
  isStickyExpanded: boolean;
}

export const useOverflowDetection = (options: IUseOverflowDetectionOptions): boolean => {
  const { containerRef, enabled, useSticky, isStickyFixed, isStickyExpanded } = options;

  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || !enabled) {
      setHasOverflow(false);
      return;
    }

    const checkOverflow = (): void => {
      const isHiddenStickyMeasurement =
        useSticky &&
        isStickyFixed &&
        !isStickyExpanded &&
        (element.scrollHeight || 0) === 0 &&
        (element.offsetHeight || 0) === 0;

      if (isHiddenStickyMeasurement) {
        return;
      }

      const nextHasOverflow = getCollapsedHasOverflow(element);

      setHasOverflow((prevOverflow) => {
        return prevOverflow === nextHasOverflow ? prevOverflow : nextHasOverflow;
      });
    };

    checkOverflow();

    const overflowObserver = observeOverflowChanges(element, checkOverflow);

    return () => {
      overflowObserver?.disconnect();
    };
  }, [useSticky, isStickyFixed, isStickyExpanded, enabled]);

  return hasOverflow;
};
